import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "notifyCheckbox", "notifyDays", "notifyDate",
    "halfwayCheckbox", "halfwayDate",
    "completionCheckbox", "completionDate",
    "startDateInput", "endDateInput",
    "startDateDisplay", "endDateDisplay"
  ]

  connect() {
    this.handleDateChange()
    this.updateNotifyDate()
    this.updateHalfwayColor()
    this.updateCompletionColor()
  }

  handleCheckboxChange() {
    this.updateNotifyDate()
    this.updateHalfwayColor()
    this.updateCompletionColor()
  }

  handleDaysChange() {
    this.updateNotifyDate()
  }

  handleDateChange() {
    if (!this.hasStartDateInputTarget || !this.hasEndDateInputTarget) return;

    const startDate = Date.parse(this.startDateInputTarget.value);
    const endDate = Date.parse(this.endDateInputTarget.value);
    
    if (isNaN(startDate) || isNaN(endDate)) return;

    const formattedStartDate = this.formatDate(startDate);
    const formattedEndDate = this.formatDate(endDate);

    if (this.hasStartDateDisplayTarget) {
      this.startDateDisplayTarget.textContent = formattedStartDate;
    }

    if (this.hasEndDateDisplayTarget) {
      this.endDateDisplayTarget.textContent = formattedEndDate;
      this.endDateDisplayTarget.dataset.endDateValue = formattedEndDate;
    }

    // Use today if startDate is in the past, otherwise use startDate
    const today = new Date();
    const startDateToUse = startDate < today.getTime() ? today.getTime() : startDate;
    const remainingDays = this.daysBetween(startDateToUse, endDate);
    const currentValue = parseInt(this.notifyDaysTarget.value) || "-";
    this.updateNotifyDaysDropdown(20, remainingDays, currentValue);

    if (this.hasHalfwayDateTarget) {
      const halfway = new Date(startDate + (endDate - startDate) / 2);
      this.halfwayDateTarget.textContent = this.formatDate(halfway);
    }

    if (this.hasCompletionDateTarget) {
      this.completionDateTarget.textContent = formattedEndDate;
    }

    this.updateNotifyDate();
    this.disableInvalidOptions();
  }

  updateNotifyDaysDropdown(maxAllowed, totalPossible, selectedValue) {
    const select = this.notifyDaysTarget;
    select.innerHTML = "";

    const dashOption = document.createElement("option");
    dashOption.value = "-";
    dashOption.textContent = "-";
    if (selectedValue === "-") dashOption.selected = true;
    select.appendChild(dashOption)

    for (let i = 1; i <= maxAllowed; i++) {
      const option = document.createElement("option");
      option.value = i;
      option.textContent = i;

      const isDisabled = i > totalPossible;
      if (isDisabled) option.disabled = true;

      if (parseInt(selectedValue) === i) option.selected = true;

      select.appendChild(option);
    }
  }

  updateNotifyDate() {
    const checked = this.notifyCheckboxTarget.checked;
    const days = parseInt(this.notifyDaysTarget.value);
    const endDateStr = this?.endDateDisplayTarget?.dataset?.endDateValue;

    if (isNaN(days) || !endDateStr) {
      this.notifyDateTarget.textContent = '';
      this.notifyDateTarget.style.color = "#1B5599";
      return;
    }

    const endDate = new Date(endDateStr);
    const notifyDate = new Date(endDate);
    notifyDate.setDate(endDate.getDate() - days);
    const formattedNotifyDate = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: '2-digit'}).format(notifyDate);

    this.notifyDateTarget.textContent = formattedNotifyDate;
    this.notifyDateTarget.style.color = checked ? "#297937" : "#1B5599";

    this.updateCheckboxState(this.notifyCheckboxTarget, this.notifyDateTarget, formattedNotifyDate);
  }

  updateHalfwayColor() {
    this.halfwayDateTarget.style.color = this.halfwayCheckboxTarget.checked ? "#297937" : "#1B5599";
  }

  updateCompletionColor() {
    this.completionDateTarget.style.color = this.completionCheckboxTarget.checked ? "#297937" : "#1B5599";
  }

  formatDate(date) {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit'
    }).format(date);
  }

  daysBetween(start, end) {
    const oneDay = 24 * 60 * 60 * 1000;
    return Math.max(0, Math.floor((end - start) / oneDay));
  }

  disableInvalidOptions() {
    const endDateStr = this?.endDateDisplayTarget?.textContent?.trim();
    const halfwayStr = this?.halfwayDateTarget?.textContent?.trim();
    const completionStr = this?.completionDateTarget?.textContent?.trim();
    const notifyDateStr = this?.notifyDateTarget?.textContent?.trim() || endDateStr;

    // Notify checkbox
    this.updateCheckboxState(this.notifyCheckboxTarget, this.notifyDateTarget, notifyDateStr);

    // Halfway checkbox
    this.updateCheckboxState(this.halfwayCheckboxTarget, this.halfwayDateTarget, halfwayStr);

    // Completion checkbox
    this.updateCheckboxState(this.completionCheckboxTarget, this.completionDateTarget, completionStr);
  }

  // Helper to enable/disable checkbox and set color
  updateCheckboxState (checkboxTarget, dateTarget, dateStr) {   
    const today = new Date();
    const compareDate = new Date(dateStr);

    if (compareDate <= today) {        
      checkboxTarget.disabled = true;
      dateTarget.style.color = "#1B5599";
    } else {        
      checkboxTarget.disabled = false;
    }
  };
}
